// Vue's <Transition> swaps *-leave-from for *-leave-to inside nextFrame(), which
// is a double-nested requestAnimationFrame, and the done() that lets
// mode="out-in" mount the incoming view is only reachable from inside that
// callback. Browsers pause rAF outright in a hidden tab, so a navigation started
// while the tab is in the background leaves the outgoing view stuck on
// *-leave-from and the incoming view never mounts at all: the content area sits
// blank under the new nav highlight until the tab is looked at again.
//
// Declaring two parameters is what makes Vue treat this as an explicit callback
// and stand down from its own transitionend detection, so completing the leave
// becomes entirely our responsibility. Vue's finishLeave clears every leave
// class before invoking done, so resolving early leaves nothing behind.
import { nextTick } from 'vue';

export function onRouteLeave(el: Element, done: () => void) {
    let settled = false;
    let timer = 0;

    const settle = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        el.removeEventListener('transitionend', onTransitionEnd);
        done();
    };

    // Never resolve synchronously from inside this hook. Under mode="out-in" Vue
    // renders an empty placeholder and only mounts the incoming view when
    // afterLeave runs instance.update(); firing that mid-patch drops the update
    // and the pane is left permanently blank. Handing control back to the
    // scheduler first is what makes the mount actually happen. nextTick rather
    // than a timer because background tabs throttle timers.
    const settleAfterPatch = () => { void nextTick().then(settle); };

    const onTransitionEnd = (e: Event) => {
        // Descendants animate too; only this element's own transition counts.
        if (e.target === el) settle();
    };

    const longestMs = Math.max(
        0,
        ...window.getComputedStyle(el).transitionDuration
            .split(',')
            .map(d => parseFloat(d) || 0),
    ) * 1000;

    // Nothing worth waiting for: no transition is actually defined (a name with
    // no matching CSS, or prefers-reduced-motion), or the tab is hidden, where
    // no one can see the animation and rAF is paused so waiting would hang.
    if (longestMs === 0 || document.hidden) {
        settleAfterPatch();
        return;
    }

    el.addEventListener('transitionend', onTransitionEnd);
    // Covers the tab being hidden partway through, after we already committed to
    // animating. setTimeout still fires in a background tab (throttled) where
    // rAF does not, so the incoming view always lands eventually.
    timer = window.setTimeout(settle, longestMs + 120);
}
